// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE GUIDED DOCUMENTS PAGE'S LISTS (Jesse, 2026-10-19; Hannah picked variant A, 2026-10-20).
// Rules: no "Nothing right now" filler (the action block shows only when something is needed, at the top, with
// a link to where); no "Edit your application" here; readable names with no file extensions, one compact row each
// (name · date · View); no "Nothing here yet" — empty groups are hidden; Identity follows the identity-card states;
// never "loan officer", "broker", "ReadyCheck", or a person reviewing the ID.
// "You sent us" (Identity, Income, Assets, Other) and "We sent you" (Disclosures & notices, then Signed documents).
// Newest first within each group.
// ─────────────────────────────────────────────────────────────────────────────

import { DOORLY_SENT_NAMES, DOORLY_SIGNED_NAMES, borrowerDocumentName } from './borrowerDocumentName';

// What Doorly sends, by document type → the name the borrower reads (constants/borrowerDocumentName, the one helper).
export const SENT_NAMES = DOORLY_SENT_NAMES;
// Signed documents (Doorly's paperwork, signed by the borrower).
export const SIGNED_TYPES = ['closing_disclosure_signed', 'itp_signed_le', 'borrower_authorization', 'psa_ab_signed', 'borrower_psa_signed', 'ab_psa_signed'];
export const SIGNED_NAMES = DOORLY_SIGNED_NAMES;
const INCOME = /^(paystubs?|w2s?|w2_|bank_statement_income|tax_return|1099|profit_loss|pnl|business_tax|social_security|pension|award_letter|voe|offer_letter|lox_income)/;
const ASSETS = /^(bank_statement$|asset_|investment|retirement|gift_letter|gift_funds|earnest_money|account_statement)/;

/** The borrower's name for a row (Hannah's review 2026-11-02, item 4): constants/borrowerDocumentName — one helper for
 *  every borrower surface; Doorly's own documents by type, the rest from the stored name ("Pay stub · Oct 17, 2026"). */
export function readableName(d) {
  return borrowerDocumentName(d);
}

/** 'identity' | 'income' | 'assets' | 'other' (you sent) — 'disclosures' | 'signed' (we sent) */
export function kindOf(d) {
  const t = String(d.document_type || '');
  if (SIGNED_TYPES.includes(t)) return 'signed';
  if (SENT_NAMES[t] || /_notice$/.test(t)) return 'disclosures';
  if (INCOME.test(t)) return 'income';
  if (ASSETS.test(t)) return 'assets';
  return 'other';
}
const newest = (a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0);

/** [{ key, title, sections: [{ key, title, items }] }] — empty sections and blocks left out. */
/**
 * The rows the page lists (Jesse, 2026-10-20): one row per file per group — a copy the upload intake marked as a
 * duplicate is left out where that file already has a row in its group (in another group it stays); two different files with the same readable name in one group are told apart — the later one(s) "(2)", "(3)",
 * oldest first — as `display_name`.
 */
export function listedDocs(docs) {
  // One row per FILE per group: intake points every copy at the first upload (duplicate_of_id), so a file and its copies
  // share that root; the oldest in each group stays. A statement given for income AND for assets shows once in each
  // (Jesse, 2026-10-20).
  const rootOf = (d) => Number(d.duplicate_of_id || d.id);
  const kept = new Set();
  const firstFirst = [...docs].sort((a, b) => new Date(a.uploaded_at || 0) - new Date(b.uploaded_at || 0) || Number(a.id) - Number(b.id));
  const shownIds = new Set();
  for (const d of firstFirst) {
    const key = `${kindOf(d)}:${rootOf(d)}`;
    if (kept.has(key)) continue;
    kept.add(key); shownIds.add(Number(d.id));
  }
  const shown = docs.filter((d) => shownIds.has(Number(d.id)));
  const seen = new Map();
  const oldest = [...shown].sort((a, b) => new Date(a.uploaded_at || 0) - new Date(b.uploaded_at || 0) || Number(a.id) - Number(b.id));
  const label = new Map();
  for (const d of oldest) {
    const n = readableName(d);
    const k = `${kindOf(d)}:${n.toLowerCase()}`;   // within a group — the same file under Income and Assets is not "(2)"
    const i = (seen.get(k) || 0) + 1;
    seen.set(k, i);
    label.set(d.id, i === 1 ? n : `${n} (${i})`);
  }
  return shown.map((d) => ({ ...d, display_name: label.get(d.id) }));
}

export function layoutA(all) {
  const docs = listedDocs(all);
  const by = (k) => docs.filter((d) => kindOf(d) === k).sort(newest);
  const blocks = [
    { key: 'you', title: 'You sent us', sections: [['income', 'Income'], ['assets', 'Assets'], ['other', 'Other']] },
    { key: 'we', title: 'We sent you', sections: [['disclosures', 'Disclosures & notices'], ['signed', 'Signed documents']] },
  ].map((b) => ({ ...b, sections: b.sections.map(([key, title]) => ({ key, title, items: by(key) })).filter((s) => s.items.length) }));
  return blocks;
}

